import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {registerTileSchema} from '#core/application/schema-registry.js';

import {generateNeutralVenueIndicator} from '#core/application/modules/tiles/neutral-venue-indicator/index.js';

import SCOREBOARD from './scoreboard.palette-schema.yaml';

registerTileSchema(SCOREBOARD);

export function generateScoreboard(TILE) {
    const BASE_SCOREBOARD = generateBaseScoreboard(TILE);

    return {
        scoreboards: {
            outrightScoreboard: generateOutrightScoreboard(TILE, BASE_SCOREBOARD),
            setBasedInplayScoreboard: generateSetBasedInplayScoreboard(TILE, BASE_SCOREBOARD),
            periodBasedInplayScoreboard: generatePeriodBasedInplayScoreboard(TILE, BASE_SCOREBOARD),
            genericInplayScoreboard: generateGenericInplayScoreboard(TILE, BASE_SCOREBOARD),
            cricketScoreboard: generateCricketScoreboard(TILE, BASE_SCOREBOARD),
            preplayScoreboard: generatePreplayScoreboard(TILE, BASE_SCOREBOARD),
        },
    };
}

export function generateBaseScoreboard(TILE) {
    const SCORE_CONTAINER = {
        height: '21px',
        minWidth: '26px',
        width: 'initial',
        maxWidth: '52px',
        borderRadius: 4,
        textAlign: 'center',
        alignContent: 'center',
    };

    return {
        scoresTextBase: {
            ...SCORE_CONTAINER,
        },
        scoresTextMain: {
            ...SCORE_CONTAINER,
            ...resolveBackground(TILE.SCOREBOARD_SCORE_BG),
            ...TILE.SCOREBOARD_SCORE_TEXT,
        },
        scoresTextSecondary: {
            ...SCORE_CONTAINER,
            ...resolveBackground(TILE.SCOREBOARD_ACTIVE_SCORE_BG),
            ...TILE.SCOREBOARD_ACTIVE_SCORE_TEXT,
        },
        segmentIndicators: {
            top: '-16px',
            text: {
                ...TILE.SCOREBOARD_SCORE_HEADER_TEXT,
            },
        },
        serverIndicator: {
            color: TILE.SCOREBOARD_LIVE_INDICATOR,
            width: '4px',
        },
        homeAwayScoresContainer: {
            itemSpacing: '4px',
        },
        scoreColumn: {
            minWidth: '4px',
            margin: '0px 0px 0px 4px',
        },
        scoreCount: {
            style: {
                scoreText: {
                    ...SCORE_CONTAINER,
                    ...resolveBackground(TILE.SCOREBOARD_SCORE_BG),
                    ...TILE.SCOREBOARD_SCORE_TEXT,
                },
            },
        },
    };
}

function generateSegmentBasedInplayScoreboard(TILE, BASE_SCOREBOARD) {
    return {
        container: {
            style: {
                padding: 0,
            },
        },
        neutralVenueIndicator: generateNeutralVenueIndicator(
            TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_NEUTRAL_VENUE_INDICATOR
        ),
        accentColor: TILE.SCOREBOARD_LIVE_INDICATOR, // use for live indicator (circle which indicates whose turn in tennis for example)
        style: {
            eventInfoHeaderCell: {
                ...TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_LIVE_TEXT,
            },
            serverDataCell: {
                fontSize: 4, // size of live indicator
            },
            totalsHeaderCell: {
                ...TILE.SCOREBOARD_ACTIVE_SCORE_HEADER_TEXT,
            },
            totalsDataCell: {
                ...BASE_SCOREBOARD.scoresTextMain,
            },
            segmentHeaderCell: {
                ...TILE.SCOREBOARD_SCORE_HEADER_TEXT,
            },
            segmentDataCell: {
                ...BASE_SCOREBOARD.scoresTextSecondary,
                ...TILE.SCOREBOARD_SEGMENTED_ACTIVE_SCORE_TEXT,
            },
            currentSegmentHeaderCell: {
                ...TILE.SCOREBOARD_SCORE_HEADER_TEXT,
            },
            currentSegmentDataCell: {
                ...BASE_SCOREBOARD.scoresTextSecondary,
                ...TILE.SCOREBOARD_SEGMENTED_ACTIVE_SCORE_TEXT,
            },
        },
        serverColumn: {
            style: {
                padding: '0 4px',
            },
        },
        eventInfoDataCell: {
            style: {
                textAlign: 'center',
                ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
            },
        },
        eventInfoColumn: {
            style: {
                alignItems: 'flex-start',
            },
        },
    };
}

function generateOutrightScoreboard(TILE) {
    return {
        eventName: {
            style: {
                textAlign: 'center',
                ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
            },
        },
    };
}

function generateSetBasedInplayScoreboard(TILE, BASE_SCOREBOARD) {
    return {
        segmentBasedInplayScoreboard: generateSegmentBasedInplayScoreboard(TILE, BASE_SCOREBOARD),
    };
}

function generatePeriodBasedInplayScoreboard(TILE, BASE_SCOREBOARD) {
    return {
        segmentBasedInplayScoreboard: generateSegmentBasedInplayScoreboard(TILE, BASE_SCOREBOARD),
    };
}

function generateGenericInplayScoreboard(TILE, BASE_SCOREBOARD) {
    return {
        inPlayClock: {
            timerFiveDigits: {
                timePart: {
                    style: {
                        ...TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_LIVE_TEXT,
                    },
                },
            },
            timerFourDigits: {
                timePart: {
                    style: {
                        ...TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_LIVE_TEXT,
                    },
                },
            },
        },
        neutralVenueIndicator: generateNeutralVenueIndicator(
            TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_NEUTRAL_VENUE_INDICATOR
        ),
        participantNameText: {
            style: {
                textAlign: 'center',
                lineHeight: '1.4', // Fix stupid fonts (like used in HR)
                ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
            },
        },
        threeParticipantsScoreboard: {
            competitorIconContainer: {
                style: {
                    height: 24,
                },
            },
            participantNameText: {
                style: {
                    textAlign: 'center',
                    lineHeight: '1.4', // Fix stupid fonts (like used in HR)
                    ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
                },
            },
            teamNameSeparator: {
                style: {
                    color: TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_SEPARATOR_COLOR,
                },
            },
        },
        style: {
            score: {
                ...BASE_SCOREBOARD.scoresTextSecondary,
            },
            scoreSeparator: {
                color: TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_SEPARATOR_COLOR,
            },
            participantName: {
                textAlign: 'center',
                ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
            },
            // segment text  '1H' '45'' ->  1H | 45'
            // what the heck it is called segment text - ask mojito
            segmentData: {
                ...TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_LIVE_TEXT,
            },
        },
    };
}

function generateCricketScoreboard(TILE, BASE_SCOREBOARD) {
    return {
        segmentBasedInplayScoreboard: merge(generateSegmentBasedInplayScoreboard(TILE, BASE_SCOREBOARD), {
            container: {
                style: {
                    padding: 8,
                },
            },
            dataRowHeight: '24px',
            headerRowHeight: '16px',
        }),
    };
}

function generatePreplayScoreboard(TILE) {
    return {
        dateTimeWrapper: {
            style: {
                ...TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_UPCOMING_TEXT,
            },
        },
        participantsBoard: {
            competitorIconContainer: {
                style: {
                    height: 24,
                },
            },
            participantNameText: {
                style: {
                    textAlign: 'center',
                    lineHeight: '1.4', // Fix stupid fonts (like used in HR)
                    ...TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_TEXT,
                },
            },
            teamNamesContainer: {
                style: {
                    width: '100%',
                },
            },
            teamName: {
                style: {
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: 0,
                    alignSelf: 'center',
                    padding: '0 10px',
                },
            },
            teamNameSeparator: {
                style: {
                    color: TILE.SCOREBOARD_PARTICIPANTS.PARTICIPANTS_SEPARATOR_COLOR,
                    alignSelf: 'center',
                },
            },
        },
        neutralVenueIndicator: generateNeutralVenueIndicator(
            TILE.SCOREBOARD_TIMING_STATUS.EVENT_TIMING_NEUTRAL_VENUE_INDICATOR
        ),
    };
}
